<template>
  <v-scale-transition>
    <div v-show="visible" class="fileupload-container">
      <div class="fileupload">
        <div ref="fileBox" class="fileupload-body" @click="openFileSelector">
          <app-file-item
            class="file"
            @click.stop
            :key="index"
            v-for="(f, index) in files"
            :type="f.type"
            :name="f.name"
            :thumbnailObjUrl="f._thumbnailObjUrl"
            @remove="removeFile(index)" />

          <div class="upload-hint" v-if="files.length === 0">
            <template v-if="isAdvancedUpload">
              <v-icon size="4rem">cloud_upload</v-icon><br>
              點擊選擇檔案或直接拖曳
            </template>
            <span v-else class="red">
              瀏覽器不支援檔案上傳😢
            </span>
          </div>
        </div>

        <div class="fileupload-action">
          <div v-if="acceptExts.length" class="caption">
            <span class="white-space-nowrap mr-2">只能選取以下檔案格式:</span><br />
            <span class="font-weight-medium">{{acceptExtsText}}</span>
          </div>
          <v-spacer />
          <v-alert
            class="pt-2 pb-2 mr-0"
            v-show="errMsg"
            transition="slide-y-transition"
            :value="true"
            type="error">
            {{errMsg}}
          </v-alert>

          <div class="white-space-nowrap font-weight-medium mr-4 ml-4" :class="{'red--text': isReachedMaxLimit}">
            {{files.length}} / {{maxFiles}}
          </div>

          <input
            ref="fileInput"
            class="file-input"
            @change="onFileInputChange"
            :accept="accept"
            type="file"
            multiple >

          <v-btn
            @click.stop="visible = false"
            flat>
            取消
          </v-btn>
          <v-btn
            color="primary"
            @click.stop="submit"
            flat>
            確認
          </v-btn>
        </div>
      </div>

    </div>
  </v-scale-transition>
</template>

<script>
import DragUploadHelper from '@/lib/drag-upload-helper';
import ImageFileHelper from '@/lib/image-file-helper';

export default {
  data() {
    return {
      visible: false,
      files: [],
      errMsg: ''
    };
  },

  props: {
    maxFiles: {
      type: Number,
      default: 10
    },

    accept: {
      type: String,
      default: ''
    },

    // valid file extension
    acceptExts: {
      type: [String, Array],
      default: () => []
    },

    maxThumbnailWidth: {
      type: Number,
      default: 200
    },

    maxThumbnailHeight: {
      type: Number,
      default: 200
    },

    thumbQuality: {
      type: Number,
      default: 1
    },

    thumbMimeType: {
      type: String,
      default: null
    }
  },

  computed: {
    isAdvancedUpload() {
      return DragUploadHelper.isAdvancedUpload();
    },

    isReachedMaxLimit() {
      return this.files.length === this.maxFiles;
    },

    acceptExtsText() {
      return [].concat(this.acceptExts).join(', ');
    }
  },

  watch: {
    visible(v) {
      if (!v) {
        this.clear();
      }
    },

    files() {
      this.files.forEach((f, index) => {
        if (this.isImage(f) && f._isThumbLoaded !== true) {
          this.createImageThumb(f, index);
        }
      });
    }
  },

  mounted() {
    let el = this.$refs.fileBox;
    let vm = this;
    this.imageFileHelper = new ImageFileHelper({mimeType: this.thumbMimeType});

    this.dragUploadHelper = new DragUploadHelper({
      el: el,
      onDragIn() {
        el.classList.add('fileupload-body--drag-enter');
      },
      onDragOut() {
        el.classList.remove('fileupload-body--drag-enter');
      },
      onDrop(e, files) {
        if (this.isReachedMaxLimit) {
          return;
        }
        el.classList.remove('fileupload-body--drag-enter');
        vm.addFiles(files);
      }
    });

    this._onWindowKeydown = this.onWindowKeydown.bind(this);
    window.addEventListener('keydown', this._onWindowKeydown);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this._onWindowKeydown);
    this.imageFileHelper.destroy();
  },

  methods: {
    isExtValid(file) {
      let extension = this.getFileExt(file);
      let acceptExts = [].concat(this.acceptExts);
      if (acceptExts.length === 0) {
        return true;
      }

      let result = acceptExts.find(ext => {
        return ext.toLowerCase() === extension;
      });
      if (result) {
        return true;
      } else {
        return false;
      }
    },

    isImage(file) {
      return file.type.startsWith('image');
    },

    getFileExt(file) {
      let nameFragments = file.name.split('.');
      if (nameFragments.length < 2) {
        return null;
      }
      return nameFragments[nameFragments.length - 1].toLowerCase();
    },

    clear() {
      this.errMsg = '';
      this.$refs.fileInput.value = null;
      this.files.forEach(f => {
        // releases object URL
        URL.revokeObjectURL(f._thumbnailObjUrl);
      });
      this.files = [];
    },

    toggle() {
      this.visible = !this.visible;
    },

    addFiles(files) {
      this.errMsg = '';
      for (let i = 0; i < files.length; i++) {
        let file = files[i];
        if (!this.isExtValid(file)) {
          this.errMsg = file.name + ' 檔案格式錯誤，無法載入';
          break;
        }
      }
      if (this.errMsg) {
        return;
      }

      for (let i = 0; i < files.length; i++) {
        if (this.isReachedMaxLimit) {
          break;
        }
        let file = files[i];
        this.files.push(file);
      }
    },

    removeFile(index) {
      // releases object URL
      URL.revokeObjectURL(this.files[index]._thumbnailObjUrl);
      this.files.splice(index, 1);
    },

    onWindowKeydown(e) {
      if (this.visible && e.keyCode === 27) {
        this.visible = false;
      }
    },

    openFileSelector() {
      if (this.isReachedMaxLimit) {
        return;
      }
      this.$refs.fileInput.click();
    },

    async onFileInputChange(e) {
      if (!e.target.files.length) {
        return;
      }
      let files = [].slice.call(e.target.files);
      this.addFiles(files);
      e.target.value = '';
      await this.$nextTick();
      // scroll to end
      this.$refs.fileBox.scrollTop = this.$refs.fileBox.scrollHeight;
    },

    async createImageThumb(file, index) {
      file._isThumbLoaded = true;
      // let blob = await this.imageFileHelper.getThumbDataUrl(file, {
      let blob = await this.imageFileHelper.getThumbBlob(file, {
        maxWidth: this.maxThumbnailWidth,
        maxHeight: this.maxThumbnailHeight,
        quality: this.thumbQuality
      });
      file._thumbnailBlob = blob;
      file._thumbnailObjUrl = URL.createObjectURL(blob);
      // file._thumbnailObjUrl = await this.imageFileHelper.getDataUrl(blob);
      // use splice to trigger view rendering
      this.files.splice(index, 1, file);
    },

    submit() {
      this.$emit('submit', this.files);
      this.visible = false;
    }
  }
};
</script>

<style lang="stylus" scoped>
.fileupload-container {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.3);
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  top: 0;
  left: 0;
}

.fileupload {
  width: 80vw;
  height: 80vh;
  display: flex;
  flex-direction: column;
  // align-items: flex-start;
  // align-content: flex-start;
  padding: 1rem;
  background-color: white;
}

.file-input {
  display: none;
}

.fileupload-body {
  overflow-y: auto;
  flex-grow: 1;
  cursor: pointer;
  border: 2px dashed rgba(0,0,0,0.5);
  padding-top: 1rem;
  padding-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
}

.fileupload-action {
  margin-top: 1rem;
  display:flex;
  align-items: center;
}

.fileupload-body--drag-enter {
  background-color: rgba(0,0,0,0.03);
  border: 2px dashed $accent-color;

  .upload-hint {
    color: $accent-color;
  }
}

.upload-hint {
  position: absolute;
  transition: 0.2s;
  top: 50%;
  left: 50%;
  text-align: center;
  transform: translate(-50%, -50%);
  font-size: 2rem;
}

.file {
  margin-bottom: 1rem;
  margin-left: 20px;
  margin-right: 20px;
}
</style>

